import debounce from 'lodash/debounce'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  TimeSeriesRequest,
  TimeSeriesResponse,
  useTimeSeriesService,
} from './timeSeriesService'

interface CacheEntry {
  request: TimeSeriesRequest
  response: TimeSeriesResponse
  timestamp: number
}

interface UseTimeSeriesDataCacheOptions {
  debounceMs?: number
  onLoadingChange?: (isLoading: boolean) => void
  onError?: (error: Error) => void
}

/**
 * Custom hook that provides debounced and cached time series data fetching.
 * useSWR is not suitable here due to the need for fine-grained control (cancelling stale requests)
 * and frequent state changes (sensor selection, time range, etc).
 *
 * Features:
 * - Debounces fetch requests to avoid excessive API calls
 * - Caches data by sensor ID and time range
 * - Only fetches new data for changed parameters
 * - Handles loading states and errors
 */
const CACHE_MAX_AGE = 5 * 60 * 1000 // 5 minutes default

export const useTimeSeriesDataCache = (
  options: UseTimeSeriesDataCacheOptions = {}
) => {
  const { debounceMs = 500, onLoadingChange, onError } = options

  const { fetchTimeSeriesData } = useTimeSeriesService()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Cache structure: Map<sensorId, CacheEntry[]>
  const cacheRef = useRef<Map<string, CacheEntry[]>>(new Map())

  // Track pending requests to avoid duplicate fetches
  const pendingRequestsRef = useRef<Map<string, Promise<TimeSeriesResponse>>>(
    new Map()
  )

  // Track the latest request parameters
  const latestRequestRef = useRef<TimeSeriesRequest | null>(null)

  // Track the current AbortController to cancel in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null)

  /**
   * Check if two time ranges overlap or are identical
   */
  const timeRangesMatch = (
    req1: TimeSeriesRequest,
    req2: TimeSeriesRequest
  ): boolean => {
    return (
      req1.start_datetime === req2.start_datetime &&
      req1.end_datetime === req2.end_datetime &&
      req1.interval === req2.interval &&
      req1.interval_unit === req2.interval_unit &&
      req1.interpolate === req2.interpolate
    )
  }

  /**
   * Find cached data for a specific sensor and time range
   */
  const findCachedData = useCallback(
    (
      sensorId: string,
      request: TimeSeriesRequest
    ): TimeSeriesResponse[string] | null => {
      const sensorCache = cacheRef.current.get(sensorId)
      if (!sensorCache) return null

      const now = Date.now()
      const validEntry = sensorCache.find(
        (entry) =>
          timeRangesMatch(entry.request, request) &&
          now - entry.timestamp < CACHE_MAX_AGE
      )

      return validEntry ? validEntry.response[sensorId] : null
    },
    []
  )

  /**
   * Add data to cache
   */
  const addToCache = useCallback(
    (
      sensorId: string,
      request: TimeSeriesRequest,
      data: TimeSeriesResponse[string]
    ) => {
      const sensorCache = cacheRef.current.get(sensorId) || []

      // Remove old entries for the same time range
      const filteredCache = sensorCache.filter(
        (entry) => !timeRangesMatch(entry.request, request)
      )

      // Add new entry
      filteredCache.push({
        request,
        response: { [sensorId]: data },
        timestamp: Date.now(),
      })

      // Keep only the most recent entries (limit to 10 per sensor)
      if (filteredCache.length > 10) {
        filteredCache.sort((a, b) => b.timestamp - a.timestamp)
        filteredCache.splice(10)
      }

      cacheRef.current.set(sensorId, filteredCache)
    },
    []
  )

  /**
   * Clear expired cache entries
   */
  const clearExpiredCache = useCallback(() => {
    const now = Date.now()
    cacheRef.current.forEach((entries, sensorId) => {
      const validEntries = entries.filter(
        (entry) => now - entry.timestamp < CACHE_MAX_AGE
      )
      if (validEntries.length > 0) {
        cacheRef.current.set(sensorId, validEntries)
      } else {
        cacheRef.current.delete(sensorId)
      }
    })
  }, [])

  /**
   * Fetch data with caching logic
   */
  const fetchWithCache = useCallback(
    async (
      request: TimeSeriesRequest,
      signal?: AbortSignal
    ): Promise<TimeSeriesResponse> => {
      // Clear expired cache entries
      clearExpiredCache()

      const result: TimeSeriesResponse = {}
      const sensorsToFetch: string[] = []

      // Check cache for each sensor
      for (const sensorId of request.entry_ids) {
        const cachedData = findCachedData(sensorId, request)
        if (cachedData) {
          result[sensorId] = cachedData
        } else {
          sensorsToFetch.push(sensorId)
        }
      }

      // If all data is cached, return immediately
      if (sensorsToFetch.length === 0) {
        return result
      }

      // Create a unique key for this request
      const requestKey = `${sensorsToFetch.join(',')}_${request.start_datetime}_${request.end_datetime}`

      // Check if there's already a pending request for these sensors
      const pendingRequest = pendingRequestsRef.current.get(requestKey)
      if (pendingRequest) {
        const pendingData = await pendingRequest
        return { ...result, ...pendingData }
      }

      // Fetch only the missing data
      const fetchPromise = fetchTimeSeriesData(
        {
          ...request,
          entry_ids: sensorsToFetch,
        },
        { signal }
      )

      // Store the pending request
      pendingRequestsRef.current.set(requestKey, fetchPromise)

      try {
        const newData = await fetchPromise

        // Add to cache
        Object.entries(newData).forEach(([sensorId, data]) => {
          addToCache(sensorId, request, data)
        })

        // Combine cached and new data
        return { ...result, ...newData }
      } finally {
        // Remove from pending requests
        pendingRequestsRef.current.delete(requestKey)
      }
    },
    [clearExpiredCache, fetchTimeSeriesData, findCachedData, addToCache]
  )

  /**
   * Store resolve/reject callbacks for pending requests
   */
  const pendingCallbacksRef = useRef<{
    resolve: (value: TimeSeriesResponse) => void
    reject: (error: Error) => void
  } | null>(null)

  /**
   * The main fetch function (debounced)
   */
  const debouncedFetch = useRef(
    debounce(async (request: TimeSeriesRequest) => {
      // Only proceed if this is still the latest request
      if (latestRequestRef.current !== request) {
        return
      }

      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController()
      const { signal } = abortControllerRef.current

      setIsLoading(true)
      setError(null)
      onLoadingChange?.(true)

      try {
        const result = await fetchWithCache(request, signal)

        // Resolve the pending promise
        if (pendingCallbacksRef.current) {
          pendingCallbacksRef.current.resolve(result)
          pendingCallbacksRef.current = null
        }

        return result
      } catch (err) {
        // Check if this was an abort - if so, don't treat it as an error
        if (err instanceof Error && err.name === 'AbortError') {
          // Don't set error state or call onError for aborted requests
          if (pendingCallbacksRef.current) {
            pendingCallbacksRef.current.reject(err)
            pendingCallbacksRef.current = null
          }
          return {} // Return empty response for aborted requests
        }

        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        onError?.(error)

        // Reject the pending promise
        if (pendingCallbacksRef.current) {
          pendingCallbacksRef.current.reject(error)
          pendingCallbacksRef.current = null
        }

        throw error
      } finally {
        // Only update loading state if this is still the latest request
        if (latestRequestRef.current === request) {
          setIsLoading(false)
          onLoadingChange?.(false)
        }

        // Clear abort controller reference if this was the active request
        if (latestRequestRef.current === request) {
          abortControllerRef.current = null
        }
      }
    }, debounceMs)
  ).current

  /**
   * Clear the cache (useful for manual refresh)
   */
  const clearCache = useCallback(() => {
    cacheRef.current.clear()
    pendingRequestsRef.current.clear()
  }, [])

  /**
   * Public fetch function that consumers will call
   * @param request - The time series request parameters
   * @param options - Optional parameters
   * @param options.bypassDebounce - If true, fetches immediately without debouncing
   */
  const fetchData = useCallback(
    async (
      request: TimeSeriesRequest,
      options?: { bypassDebounce?: boolean }
    ): Promise<TimeSeriesResponse> => {
      // Store the latest request
      latestRequestRef.current = request

      // Cancel any pending debounced calls and callbacks
      debouncedFetch.cancel()

      // If there's a pending callback, reject it
      if (pendingCallbacksRef.current) {
        pendingCallbacksRef.current.reject(new Error('Request cancelled'))
        pendingCallbacksRef.current = null
      }

      // If no sensors selected, return empty immediately
      if (request.entry_ids.length === 0) {
        setIsLoading(false)
        onLoadingChange?.(false)
        return {}
      }

      // Check if all data is cached
      const immediateResult: TimeSeriesResponse = {}
      let hasAllCached = true

      for (const sensorId of request.entry_ids) {
        const cachedData = findCachedData(sensorId, request)
        if (cachedData) {
          immediateResult[sensorId] = cachedData
        } else {
          hasAllCached = false
        }
      }

      // If all data is cached, return it immediately
      if (
        !options?.bypassDebounce &&
        hasAllCached &&
        request.entry_ids.length > 0
      ) {
        setIsLoading(false)
        onLoadingChange?.(false)
        return immediateResult
      }

      // Set loading state
      setIsLoading(true)
      onLoadingChange?.(true)

      // If bypassing debounce, fetch immediately
      if (options?.bypassDebounce) {
        // Make sure to clear the cache totally to avoid stale data
        clearCache()
        // Cancel any existing request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort()
        }

        // Create new AbortController for this request
        abortControllerRef.current = new AbortController()
        const { signal } = abortControllerRef.current

        try {
          return await fetchWithCache(request, signal)
        } catch (err) {
          // Check if this was an abort
          if (err instanceof Error && err.name === 'AbortError') {
            return {} // Return empty response for aborted requests
          }

          const error = err instanceof Error ? err : new Error('Unknown error')
          setError(error)
          onError?.(error)
          throw error
        } finally {
          setIsLoading(false)
          onLoadingChange?.(false)
          abortControllerRef.current = null
        }
      }

      // Otherwise, use debounced fetch
      return new Promise<TimeSeriesResponse>((resolve, reject) => {
        pendingCallbacksRef.current = { resolve, reject }

        // Call the debounced function
        debouncedFetch(request)
      })
    },
    [
      debouncedFetch,
      onLoadingChange,
      findCachedData,
      fetchWithCache,
      onError,
      clearCache,
    ]
  )

  /**
   * Get current cache statistics
   */
  const getCacheStats = useCallback(() => {
    let totalEntries = 0
    const totalSensors = cacheRef.current.size

    cacheRef.current.forEach((entries) => {
      totalEntries += entries.length
    })

    return {
      totalSensors,
      totalEntries,
      cacheSize: cacheRef.current.size,
    }
  }, [])

  // Cleanup function to abort any in-flight requests
  useEffect(() => {
    return () => {
      // Cancel any pending requests on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      debouncedFetch.cancel()
    }
  }, [debouncedFetch])

  return {
    fetchData,
    isLoading,
    error,
    clearCache,
    getCacheStats,
  }
}
